
import React from "react";
import './CalculatorSliders.css';

const getMinMonthlyPayment = (debtBalance, debtAPR) => {
  const yearlyInterest = debtBalance * (debtAPR + 0.1);
  const monthlyInterest = yearlyInterest / 12;
  const roundedMonthlyInterest = Math.ceil(monthlyInterest / 100) * 100;
  return Math.round(roundedMonthlyInterest);
}

const getSliderBackground = (sliderValue, sliderMinValue, sliderMaxValue, hasError) => {
  const sliderPercentage = Math.round(((sliderValue - sliderMinValue)/(sliderMaxValue - sliderMinValue)) * 100);
  const sliderColor = hasError ? '#D74037' : '#B5058E';
  return `linear-gradient(to right,  ${sliderColor} 0%, ${sliderColor} ${sliderPercentage}%, #F7F8FA ${sliderPercentage}%, #F7F8FA 100%`
}

const CalculatorTooltip = ({title, description}) => {
  return (
    <React.Fragment>
      <div className='tooltip-container'>
          <p className='title'>{title}</p>
          <span className='tooltip1' data-text={description}><img data-text={description} alt='' /></span>
      </div>
    </React.Fragment>
  )
}

const CREDIT_SCORE_EXCELLENT = '720-850';
const CREDIT_SCORE_GOOD = '660-719';
const CREDIT_SCORE_FAIR = '580-659';
const CREDIT_SCORE_POOR = '300-579';

function CreditScoreSelector({creditScoreRef, onChange}) {
  const [selectedCreditScore, setSelectedCreditScore] = React.useState(creditScoreRef.current);

  function getCreditScoreButtonClass(creditScore) {
    return selectedCreditScore === creditScore ? 'credit-score active' : 'credit-score';
  }

  function onCreditScoreSelected(creditScore) {
    creditScoreRef.current = creditScore;
    setSelectedCreditScore(creditScore);
    onChange();
  }

  function onCreditScoreSelectChanged(evt) {
    let creditScore = evt.target.value;
    creditScoreRef.current = creditScore;
    setSelectedCreditScore(creditScore);
    onChange();
  }

  return (
    <div className='credit-score-container'>
      <CalculatorTooltip title='What is your credit score?' description='We use your credit-score to determine the best tools to help with debt reduction/elimination.' />
      <div className='credit-score-button-container'>
        <button className={getCreditScoreButtonClass(CREDIT_SCORE_POOR)} onClick={() => onCreditScoreSelected(CREDIT_SCORE_POOR)}>
          {CREDIT_SCORE_POOR}
        </button>
        <button className={getCreditScoreButtonClass(CREDIT_SCORE_FAIR)} onClick={() => onCreditScoreSelected(CREDIT_SCORE_FAIR)}>
          {CREDIT_SCORE_FAIR}
        </button>
        <button className={getCreditScoreButtonClass(CREDIT_SCORE_GOOD)} onClick={() => onCreditScoreSelected(CREDIT_SCORE_GOOD)}>
          {CREDIT_SCORE_GOOD}
        </button>
        <button className={getCreditScoreButtonClass(CREDIT_SCORE_EXCELLENT)} onClick={() => onCreditScoreSelected(CREDIT_SCORE_EXCELLENT)}>
          {CREDIT_SCORE_EXCELLENT}
        </button>
      </div>
      <div className='credit-score-select-container'>
        <select onChange={onCreditScoreSelectChanged}>
          <option selected={selectedCreditScore === CREDIT_SCORE_EXCELLENT} value={CREDIT_SCORE_EXCELLENT}>{CREDIT_SCORE_EXCELLENT}</option>
          <option selected={selectedCreditScore === CREDIT_SCORE_GOOD} value={CREDIT_SCORE_GOOD}>{CREDIT_SCORE_GOOD}</option>
          <option selected={selectedCreditScore === CREDIT_SCORE_FAIR} value={CREDIT_SCORE_FAIR}>{CREDIT_SCORE_FAIR}</option>
          <option selected={selectedCreditScore === CREDIT_SCORE_POOR} value={CREDIT_SCORE_POOR}>{CREDIT_SCORE_POOR}</option>
        </select>
      </div>
    </div>
  );
}

function CalculatorSlider({tooltipTitle, tooltipDescription, sliderRef, valueRef, hasErrorRef, errorRef, sliderOnChangeHandler, min, max, step, defaultValue, isDate=false, isPercentage=false}){

  const nth = (d) => {
    if (d > 3 && d < 21) return 'th';
    switch (d % 10) {
        case 1:  return "st";
        case 2:  return "nd";
        case 3:  return "rd";
        default: return "th";
    }
  }

  const update = React.useCallback(() => {
    if (sliderRef.current === null || sliderRef.current === undefined) {
      return 
    }
    
    sliderRef.current.style.background = getSliderBackground(sliderRef.current.value, sliderRef.current.min, sliderRef.current.max, hasErrorRef?.current ?? false);
    sliderRef.current.style.setProperty('--color', hasErrorRef?.current === true ? '#D74037' : '#B5058E');
    sliderRef.current.style.setProperty('--bgcolor', hasErrorRef?.current === true ? '#D7403750' : '#B5058E50');

    let value = parseInt(sliderRef.current.value);
    let valueStr = value.toLocaleString();
    valueRef.current.innerText = isPercentage ? `${valueStr}%` : (isDate ? `${valueStr}${nth(value)}` : `$${valueStr}`);
  }, [sliderRef, valueRef, hasErrorRef, isPercentage, isDate])

  React.useEffect(() => {
    update();
  }, [update]);


  function onChange(e) {
    update();
    sliderOnChangeHandler();
  }

  return (
    <div className='calculator-slider'>
      <CalculatorTooltip title={tooltipTitle} description={tooltipDescription} />
      
      <div className='calculator-slider-container'>
        <input type='range' ref={sliderRef}  min={min} max={max} step={step} defaultValue={defaultValue} onChange={onChange} />
        <div ref={valueRef} className='calculator-slider-value' />
      </div>
      <div className='error' ref={errorRef}></div>
      
    </div>
  );
}

function CalculatorSliders({creditScoreRef, debtRef, debtAPRRef, debtConsolidationAPRRef, seraAPRRef, monthlyIncomeRef, monthlyNonCCExpensesRef, nonCCExpensesDeductionDOMRef, monthlyPaymentRef, monthlySavingsRef, useSeraBasicFlatFeesRef, seraBasicPercentageFeesRef, seraBasicFlatFeesRef, useSeraSoloFlatFeesRef, seraSoloPercentageFeesRef, seraSoloFlatFeesRef, hasInputErrorRef, onChange, errorMsg, defaultDebtValue, defaultDebtAPRValue, defaultDebtConsolidationAPRValue, defaultSeraAPRValue, defaultMonthlyIncomeValue, defaultMonthlyNonCCExpensesValue, defaultNonCCExpensesDeductionDOMValue, defaultMonthlyPaymentValue, defaultMonthlySavingsValue, defaultUseSeraBasicFlatFeesValue, defaultUseSeraSoloFlatFeesValue, defaultSeraPercentageFeesValue, defaultSeraFlatFeesValue, loanAPR, isAdvanced=false}) {
  const MIN_DEBT = 5000;
  const MAX_DEBT = 100000;
  const STEP_DEBT = 1000;

  const MIN_DEBT_APR = 10;
  const MAX_DEBT_APR = 50;
  const STEP_DEBT_APR = 1;

  const MIN_DEBT_CONSOLIDATION_APR = 10;
  const MAX_DEBT_CONSOLIDATION_APR = 50;
  const STEP_DEBT_CONSOLIDATION_APR = 1;

  const MIN_SERA_APR = 10;
  const MAX_SERA_APR = 50;
  const STEP_SERA_APR = 1;

  const MIN_INCOME = 3000;
  const MAX_INCOME = 20000;
  const STEP_INCOME = 1000;

  const MIN_NON_CC_EXPENSES = 1000;
  const MAX_NON_CC_EXPENSES = 10000;
  const STEP_NON_CC_EXPENSES = 50;

  const MIN_NON_CC_EXPENSES_DEDUCTION_DOM = 1;
  const MAX_NON_CC_EXPENSES_DEDUCTION_DOM = 14;
  const STEP_NON_CC_EXPENSES_DEDUCTION_DOM = 1;

  const MIN_PAYMENT = 50;
  const MAX_PAYMENT = 4000;
  const STEP_PAYMENT = 50;

  const MIN_SAVINGS = 0;
  const MAX_SAVINGS = 1000;
  const STEP_SAVINGS = 50;

  const MIN_SERA_PERCENTAGE_FEES = 1;
  const MAX_SERA_PERCENTAGE_FEES = 20;
  const STEP_SERA_PERCENTAGE_FEES = 1;

  const MIN_SERA_FLAT_FEES = 1;
  const MAX_SERA_FLAT_FEES = 20;
  const STEP_SERA_FLAT_FEES = 1;

  //const creditScoreRef = React.useRef(CREDIT_SCORE_EXCELLENT);

  const debtValueRef = React.useRef();
  const debtAPRValueRef = React.useRef();
  const debtConsolidationAPRValueRef = React.useRef();
  const seraAPRValueRef = React.useRef();
  const monthlyIncomeValueRef = React.useRef();
  const monthlyNonCCExpensesValueRef = React.useRef();
  const nonCCExpensesDeductionDOMValueRef = React.useRef();
  const monthlyPaymentValueRef = React.useRef();
  const monthlySavingsValueRef = React.useRef();

  const monthlyNonCCExpensesErrorRef = React.useRef();
  const monthlyNonCCExpensesHasErrorRef = React.useRef(false);

  const monthlyPaymentErrorRef = React.useRef();
  const monthlyPaymentHasErrorRef = React.useRef(false);

  const seraBasicPercentageFeesValueRef = React.useRef();
  const seraBasicFlatFeesValueRef = React.useRef();
  const seraSoloPercentageFeesValueRef = React.useRef();
  const seraSoloFlatFeesValueRef = React.useRef();

  const seraBasicPercentageFeesContainerRef = React.useRef();
  const seraBasicFlatFeesContainerRef = React.useRef();
  const seraSoloPercentageFeesContainerRef = React.useRef();
  const seraSoloFlatFeesContainerRef = React.useRef();

  function updateSliderState(hasError, errorMessage, sliderRef, errorRef, hasErrorRef) {
    errorRef.current.style.display = hasError ? 'block' : 'none';
    errorRef.current.innerText = hasError ? errorMessage : '';
    hasErrorRef.current = hasError;

    sliderRef.current.style.background = getSliderBackground(sliderRef.current.value, sliderRef.current.min, sliderRef.current.max, hasError);  
    sliderRef.current.style.setProperty('--color', hasError ? '#D74037' : '#B5058E');
    sliderRef.current.style.setProperty('--bgcolor', hasError ? '#D7403750' : '#B5058E50');
  }

  function validateMonthlyPayment() {
    if (!debtRef.current || !monthlyPaymentRef.current) {
      return;
    }
    let debtAPR = (debtAPRRef.current && debtAPRRef.current.value) ? debtAPRRef.current.value : defaultDebtAPRValue;
    let newDebtValue = parseInt(debtRef.current.value);
    let currentMonthlyPaymentValue = parseInt(monthlyPaymentRef.current.value);
    const newMinMonthlyPayment = getMinMonthlyPayment(newDebtValue, parseInt(debtAPR)/100);
    console.log('currentMonthlyPaymentValue: ' + currentMonthlyPaymentValue + ', newDebtValue: ' + newDebtValue + ', debtAPR: ' + debtAPR/100);

    let hasError = currentMonthlyPaymentValue < newMinMonthlyPayment;
    let errorMessage = `This needs to be over $${parseInt(newMinMonthlyPayment).toLocaleString()} to cover your monthly minimum`;

    updateSliderState(hasError, errorMessage, monthlyPaymentRef, monthlyPaymentErrorRef, monthlyPaymentHasErrorRef);
    return hasError;
  }

  function validateNonCCExpenses() {
    if (!isAdvanced || !monthlyIncomeRef.current || !monthlyPaymentRef.current || !monthlySavingsRef.current || !monthlyNonCCExpensesRef.current) {
      return;
    }

    let currentMonthlyIncomeValue = parseInt(monthlyIncomeRef.current.value);
    let currentMonthlyPaymentValue = parseInt(monthlyPaymentRef.current.value);
    let currentMonthlySavingsValue = parseInt(monthlySavingsRef.current.value);
    let currentMonthlyNonCCExpensesValue = parseInt(monthlyNonCCExpensesRef.current.value);

    let totalExpenses = currentMonthlyNonCCExpensesValue + currentMonthlyPaymentValue + currentMonthlySavingsValue;
    let hasError = totalExpenses >= currentMonthlyIncomeValue;
    let errorMessage = hasError ? `This needs to be under $${(currentMonthlyIncomeValue - (currentMonthlySavingsValue + currentMonthlyPaymentValue)).toLocaleString()} for the current income, payment and savings selections` : '';

    updateSliderState(hasError, errorMessage, monthlyNonCCExpensesRef, monthlyNonCCExpensesErrorRef, monthlyNonCCExpensesHasErrorRef);
    return hasError;
  }

  const onChangeHandler = () => {
    let paymentErr = validateMonthlyPayment();
    let ccErr = validateNonCCExpenses();
    hasInputErrorRef.current = paymentErr || ccErr;
    onChange();
  }

  const onFeesTypeToggleHandler = (callHandler = true) => {
    if (!seraBasicPercentageFeesContainerRef.current || !seraBasicFlatFeesContainerRef.current || !useSeraBasicFlatFeesRef.current || !seraSoloPercentageFeesContainerRef.current || !seraSoloFlatFeesContainerRef.current || !useSeraSoloFlatFeesRef.current) {
      return;
    }
    seraBasicPercentageFeesContainerRef.current.style.display = useSeraBasicFlatFeesRef.current.checked ? 'none' : 'block';
    seraBasicFlatFeesContainerRef.current.style.display = useSeraBasicFlatFeesRef.current.checked ? 'block' : 'none';
    seraSoloPercentageFeesContainerRef.current.style.display = useSeraSoloFlatFeesRef.current.checked ? 'none' : 'block';
    seraSoloFlatFeesContainerRef.current.style.display = useSeraSoloFlatFeesRef.current.checked ? 'block' : 'none';

    if (callHandler) {
      onChangeHandler();
    }
  }

  React.useEffect(() => {
    onFeesTypeToggleHandler(false);
  }, []);

  return (
    <div className='calculator-sliders'>
      {!isAdvanced && (
        <CreditScoreSelector creditScoreRef={creditScoreRef} onChange={onChangeHandler} />
      )}

      <CalculatorSlider tooltipTitle='Debt balance?' tooltipDescription='Enter the total debt you have on high interest credit cards.' sliderRef={debtRef} valueRef={debtValueRef} sliderOnChangeHandler={onChangeHandler} min={MIN_DEBT} max={MAX_DEBT} step={STEP_DEBT} defaultValue={defaultDebtValue} />

      {isAdvanced && (
        <React.Fragment>
        <CalculatorSlider tooltipTitle='Debt APR?' tooltipDescription='Enter the APR of the debt you are trying to pay off'
        sliderRef={debtAPRRef} valueRef={debtAPRValueRef} sliderOnChangeHandler={onChangeHandler} min={MIN_DEBT_APR} max={MAX_DEBT_APR} step={STEP_DEBT_APR} defaultValue={defaultDebtAPRValue} isPercentage={true} />    

        <CalculatorSlider tooltipTitle='Debt Consolidation APR?' tooltipDescription='Enter the APR of the debt consolidation loan' sliderRef={debtConsolidationAPRRef} valueRef={debtConsolidationAPRValueRef} sliderOnChangeHandler={onChangeHandler} min={MIN_DEBT_CONSOLIDATION_APR} max={MAX_DEBT_CONSOLIDATION_APR} step={STEP_DEBT_CONSOLIDATION_APR} defaultValue={defaultDebtConsolidationAPRValue} isPercentage={true} />

        <CalculatorSlider tooltipTitle='Sera APR?' tooltipDescription='Enter the APR that Sera can offer'
        sliderRef={seraAPRRef} valueRef={seraAPRValueRef} sliderOnChangeHandler={onChangeHandler} min={MIN_SERA_APR} max={MAX_SERA_APR} step={STEP_SERA_APR} defaultValue={defaultSeraAPRValue} isPercentage={true} />
        </React.Fragment>  
      )}

      <CalculatorSlider tooltipTitle='Monthly Income?' tooltipDescription='Enter your monthly net income after taxes.'
        sliderRef={monthlyIncomeRef} valueRef={monthlyIncomeValueRef} sliderOnChangeHandler={onChangeHandler} min={MIN_INCOME} max={MAX_INCOME} step={STEP_INCOME} defaultValue={defaultMonthlyIncomeValue} />
      
      {isAdvanced && (
        <React.Fragment>
        <CalculatorSlider tooltipTitle='Monthly non-CC expenses?' tooltipDescription='Enter the total expenses that cannot be put on the credit card (rent/mortgage, car-payment etc.)' sliderRef={monthlyNonCCExpensesRef} valueRef={monthlyNonCCExpensesValueRef} sliderOnChangeHandler={onChangeHandler} min={MIN_NON_CC_EXPENSES} max={MAX_NON_CC_EXPENSES} step={STEP_NON_CC_EXPENSES} defaultValue={defaultMonthlyNonCCExpensesValue} hasErrorRef={monthlyNonCCExpensesHasErrorRef} errorRef={monthlyNonCCExpensesErrorRef}/>
        
        <CalculatorSlider tooltipTitle='Non-CC expenses deduction DOM?' tooltipDescription='Enter the day of the month when you want the non-CC expenses to be deducted)' sliderRef={nonCCExpensesDeductionDOMRef} valueRef={nonCCExpensesDeductionDOMValueRef} sliderOnChangeHandler={onChangeHandler} min={MIN_NON_CC_EXPENSES_DEDUCTION_DOM} max={MAX_NON_CC_EXPENSES_DEDUCTION_DOM} step={STEP_NON_CC_EXPENSES_DEDUCTION_DOM} defaultValue={defaultNonCCExpensesDeductionDOMValue} isDate={true} />  
        
        </React.Fragment>      
      )}

      <CalculatorSlider tooltipTitle='Desired monthly payment?' tooltipDescription='How much would you like to pay towards your debt? This must be at least your minimum monthly payment.' sliderRef={monthlyPaymentRef} valueRef={monthlyPaymentValueRef} sliderOnChangeHandler={onChangeHandler} min={MIN_PAYMENT} max={MAX_PAYMENT} step={STEP_PAYMENT} defaultValue={defaultMonthlyPaymentValue} hasErrorRef={monthlyPaymentHasErrorRef} errorRef={monthlyPaymentErrorRef} />

      <CalculatorSlider tooltipTitle='Desired extra savings?' tooltipDescription='How much extra would you like to save each month? With Sera, you can save for goals or emergencies and still pay down your debt.' sliderRef={monthlySavingsRef} valueRef={monthlySavingsValueRef} sliderOnChangeHandler={onChangeHandler} min={MIN_SAVINGS} max={MAX_SAVINGS} step={STEP_SAVINGS} defaultValue={defaultMonthlySavingsValue} />

      {isAdvanced && (
        <React.Fragment>
          <div className='fees-container'>
            <div ref={seraBasicPercentageFeesContainerRef}>
              <CalculatorSlider tooltipTitle='Sera Basic fees (%)' tooltipDescription='Specify the % of savings to be charged as fees' sliderRef={seraBasicPercentageFeesRef} valueRef={seraBasicPercentageFeesValueRef} sliderOnChangeHandler={onChangeHandler} min={MIN_SERA_PERCENTAGE_FEES} max={MAX_SERA_PERCENTAGE_FEES} step={STEP_SERA_PERCENTAGE_FEES} defaultValue={defaultSeraPercentageFeesValue} isPercentage={true} />
            </div>
            <div ref={seraBasicFlatFeesContainerRef}>
              <CalculatorSlider tooltipTitle='Sera Basic fees (flat)' tooltipDescription='Specify the flat monthly fees' sliderRef={seraBasicFlatFeesRef} valueRef={seraBasicFlatFeesValueRef} sliderOnChangeHandler={onChangeHandler} min={MIN_SERA_FLAT_FEES} max={MAX_SERA_FLAT_FEES} step={STEP_SERA_FLAT_FEES} defaultValue={defaultSeraFlatFeesValue} />
            </div>
            <div className='tooltip-container'>
              <p className='title'>Use flat fees for Sera Basic 
                <input ref={useSeraBasicFlatFeesRef} type="checkbox" defaultChecked={defaultUseSeraBasicFlatFeesValue} onChange={() => onFeesTypeToggleHandler()} />
              </p>
            </div>
          </div>
          <div className='fees-container'>
            <div ref={seraSoloPercentageFeesContainerRef}>
              <CalculatorSlider tooltipTitle='Sera Solo fees (%)' tooltipDescription='Specify the % of savings to be charged as fees' sliderRef={seraSoloPercentageFeesRef} valueRef={seraSoloPercentageFeesValueRef} sliderOnChangeHandler={onChangeHandler} min={MIN_SERA_PERCENTAGE_FEES} max={MAX_SERA_PERCENTAGE_FEES} step={STEP_SERA_PERCENTAGE_FEES} defaultValue={defaultSeraPercentageFeesValue} isPercentage={true} />
            </div>
            <div ref={seraSoloFlatFeesContainerRef}>
              <CalculatorSlider tooltipTitle='Sera Solo fees (flat)' tooltipDescription='Specify the flat monthly fees' sliderRef={seraSoloFlatFeesRef} valueRef={seraSoloFlatFeesValueRef} sliderOnChangeHandler={onChangeHandler} min={MIN_SERA_FLAT_FEES} max={MAX_SERA_FLAT_FEES} step={STEP_SERA_FLAT_FEES} defaultValue={defaultSeraFlatFeesValue} />
            </div>
            <div className='tooltip-container'>
              <p className='title'>Use flat fees for Sera Solo 
                <input ref={useSeraSoloFlatFeesRef} type="checkbox" defaultChecked={defaultUseSeraSoloFlatFeesValue} onChange={() => onFeesTypeToggleHandler()} />
              </p>
            </div>
          </div>
        </React.Fragment>
      )}
      
      {!isAdvanced && (
        <p className='apr-info'>{`${loanAPR > 0 ? 'Average Sera APR ' + loanAPR + '%, ' : ''}`} Average debt APR 22%</p>
      )}
      <div className='error'>{errorMsg}</div>
    </div>
  );
};



export default CalculatorSliders;